/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { cached, tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { type Registry as Services, service } from '@ember/service';
import type { ModelRegistry } from 'ember-data/types/registries/model';

type OccasionType = 'booking' | 'booking-request' | 'enquiry';

interface LanguageOption {
  code: string | null;
  label: string;
}

const LANGUAGE_CODES = [
  'en',
  'af',
  'am',
  'ar',
  'az',
  'be',
  'bg',
  'bn',
  'bs',
  'ca',
  'cs',
  'cy',
  'da',
  'de',
  'el',
  'es',
  'et',
  'eu',
  'fa',
  'fi',
  'fr',
  'ga',
  'gl',
  'gu',
  'he',
  'hi',
  'hr',
  'hu',
  'hy',
  'id',
  'is',
  'it',
  'ja',
  'ka',
  'kk',
  'km',
  'kn',
  'ko',
  'lt',
  'lv',
  'mk',
  'ml',
  'mn',
  'mr',
  'ms',
  'mt',
  'my',
  'nb',
  'ne',
  'nl',
  'pa',
  'pl',
  'pt',
  'ro',
  'ru',
  'si',
  'sk',
  'sl',
  'sq',
  'sr',
  'sv',
  'sw',
  'ta',
  'te',
  'th',
  'tl',
  'tr',
  'uk',
  'ur',
  'uz',
  'vi',
  'zh',
] as const;

const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });

function languageName(code: string): string {
  return displayNames.of(code) ?? code;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', label: languageName('en') },
  ...LANGUAGE_CODES.filter((c) => c !== 'en')
    .map((code) => ({ code, label: languageName(code) }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

interface IArgs {
  occasion:
    | ModelRegistry['booking']
    | ModelRegistry['booking-request']
    | ModelRegistry['enquiry'];
  occasionType: OccasionType;
}

interface BookingAiSignature {
  Element: HTMLDivElement;
  Args: IArgs;
}

export default class UiBookingAiComponent extends Component<BookingAiSignature> {
  @service intl!: Services['intl'];
  @service featureFlags!: Services['feature-flags'];
  @service('http-client') httpClient!: Services['http-client'];
  @service notifications!: Services['notifications'];
  @service sentry!: Services['sentry'];

  @service('repositories/ai-setting')
  aiSettingRepository!: Services['repositories/ai-setting'];

  @tracked isTogglingAutoReply = false;
  @tracked isSavingLanguage = false;
  @tracked autoReplyOverride?: boolean;

  @cached
  get canSeeAutoReply(): boolean {
    return this.featureFlags.gate.enabled('ai_auto_reply');
  }

  get autoReplyEnabledForProperty(): boolean {
    const propertyId = this.args.occasion.belongsTo('property').id();
    return this.aiSettingRepository.resolveAutoReplyForProperty(propertyId);
  }

  @cached
  get context(): ModelRegistry['ai-occasion-context'] | undefined {
    return this.args.occasion.aiOccasionContext;
  }

  @cached
  get sentimentLabel(): string {
    const sentiment = this.context?.sentiment;
    if (!sentiment) return '';

    return this.intl.t(
      `action_bookings_ai.conversation_analysis.sentiments.${sentiment}`,
    ) as string;
  }

  @cached
  get sentimentClass(): string {
    return this.context?.sentiment ?? '';
  }

  @cached
  get responseTimeFormatted(): string {
    const seconds = this.context?.averageResponseTimeSeconds;
    if (!seconds) return '-';

    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)} min avg`;

    return `${Math.round(seconds / 3600)}h avg`;
  }

  @cached
  get aiMessagesSummary(): string {
    const aiCount = this.context?.aiMessageCount ?? 0;
    const totalMessages = this.args.occasion.messages?.length ?? 0;

    return `${aiCount} of ${totalMessages}`;
  }

  @cached
  get summary(): string | undefined {
    return this.context?.summary;
  }

  @cached
  get autoReplyEnabled(): boolean {
    return (
      this.autoReplyOverride ??
      this.context?.autoReplyEnabled ??
      this.autoReplyEnabledForProperty
    );
  }

  @cached
  get isAutoReplyToggleDisabled(): boolean {
    return !this.autoReplyEnabledForProperty || this.isTogglingAutoReply;
  }

  get languageOptions(): LanguageOption[] {
    const detected = this.context?.detectedLanguage;
    const detectedName =
      LANGUAGE_OPTIONS.find((l) => l.code === detected)?.label ?? detected;

    const autoLabel = detected
      ? `${this.intl.t('action_bookings_ai.language.auto_detected')} (${detectedName})`
      : (this.intl.t('action_bookings_ai.language.auto_detect') as string);

    return [{ code: null, label: autoLabel }, ...LANGUAGE_OPTIONS];
  }

  get selectedLanguage(): LanguageOption {
    const override = this.context?.languageOverride;

    if (override) {
      const match = this.languageOptions.find((l) => l.code === override);
      if (match) return match;
    }

    return this.languageOptions[0] as LanguageOption;
  }

  @action
  @waitFor
  async changeLanguage(option: LanguageOption): Promise<void> {
    if (this.isSavingLanguage) return;

    this.isSavingLanguage = true;
    const previousOverride = this.context?.languageOverride;

    try {
      if (this.context) {
        this.context.languageOverride = option.code ?? undefined;
      }

      await this.postOccasionContext({ language_override: option.code });

      if (!this.context) {
        await this.args.occasion.reload();
      }

      this.notifications.info('action_bookings_ai.language.saved');
    } catch (error) {
      if (this.context) {
        this.context.languageOverride = previousOverride;
      }
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.isSavingLanguage = false;
    }
  }

  @action
  @waitFor
  async toggleAutoReply(): Promise<void> {
    if (this.isAutoReplyToggleDisabled) return;

    this.isTogglingAutoReply = true;
    const newValue = !this.autoReplyEnabled;
    const previousOverride = this.autoReplyOverride;
    const previousContextValue = this.context?.autoReplyEnabled;

    try {
      this.autoReplyOverride = newValue;

      if (this.context) {
        this.context.autoReplyEnabled = newValue;
      }

      await this.postOccasionContext({ auto_reply_enabled: newValue });

      if (!this.context) {
        await this.args.occasion.reload();
      }

      const key = newValue
        ? 'action_bookings_ai.auto_reply.enabled'
        : 'action_bookings_ai.auto_reply.disabled';
      this.notifications.info(key);
    } catch (error) {
      this.autoReplyOverride = previousOverride;
      if (this.context && previousContextValue !== undefined) {
        this.context.autoReplyEnabled = previousContextValue;
      }
      this.sentry.captureException(error);
      this.notifications.error();
    } finally {
      this.isTogglingAutoReply = false;
    }
  }

  private async postOccasionContext(
    attributes: Record<string, unknown>,
  ): Promise<void> {
    const relationshipKey = this.args.occasionType;
    const pluralType =
      relationshipKey === 'enquiry' ? 'enquiries' : `${relationshipKey}s`;

    const result = await this.httpClient.postJSONApi(
      'v2/ai_occasion_contexts',
      {
        type: 'ai-occasion-contexts',
        attributes,
        relationships: {
          [relationshipKey]: {
            data: { type: pluralType, id: this.args.occasion.id },
          },
        },
      },
    );

    if (
      result &&
      typeof result === 'object' &&
      (result as Record<string, unknown>)['isError']
    )
      throw result;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Ai': typeof UiBookingAiComponent;
  }
}
